/**
 * AdminFounding — GUI counterpart to `scripts/founding_donor_update.py`.
 *
 * Route: /admin/founding  (auth-gated: user.is_admin must be true)
 *
 * Terri lands here to:
 *   - see every founding pledge (seed + pending + paid) in one table
 *   - fix a typo (full_name, email, alma_mater)
 *   - add a tribute or photo to a seed row
 *   - hide a row from the public wall (without deleting the record)
 *   - remove test/junk rows (pending/seed only — paid pledges are locked)
 *
 * Every mutation is captured in the audit log by the backend.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  ShieldCheck,
  ArrowLeft,
  Pencil,
  EyeOff,
  Eye,
  Trash2,
  UserCircle2,
  Send,
} from "lucide-react";

import { useAuth } from "../lib/AuthContext";
import { Navbar } from "../components/Navbar";
import { EmSwash } from "../components/EmSwash";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { useNoIndex } from "../lib/useNoIndex";
import { FoundingWindowPanel } from "../components/admin/FoundingWindowPanel";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const STATUS_COLORS = {
  paid: "bg-grass/15 text-grass ring-grass/30",
  seed: "bg-cobalt/12 text-cobalt ring-cobalt/25",
  pending: "bg-tangerine/12 text-tangerine ring-tangerine/25",
};

const fmtMoney = (cents) => {
  if (!cents) return "—";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
};

const truncate = (s, n = 60) => {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n).trim()}…` : s;
};

export default function AdminFounding() {
  useNoIndex();
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editing, setEditing] = useState(null); // pledge being edited, or null
  const [deleting, setDeleting] = useState(null); // pledge being confirmed for delete
  const [busy, setBusy] = useState(false);

  // Filter chip — "all" | "seed" | "pending" | "paid" | "hidden"
  const [filter, setFilter] = useState("all");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await client.get("/admin/founding");
      setPledges(res.data?.pledges || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load founding pledges.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true, state: { from: "/admin/founding" } });
    } else if (!user.is_admin) {
      navigate("/dashboard", { replace: true });
    } else {
      refresh();
    }
  }, [authLoading, user, navigate, refresh]);

  const filtered = useMemo(() => {
    if (filter === "all") return pledges;
    if (filter === "hidden") return pledges.filter((p) => p.hidden_from_wall);
    return pledges.filter((p) => p.status === filter);
  }, [pledges, filter]);

  const counts = useMemo(() => {
    const c = { all: pledges.length, seed: 0, pending: 0, paid: 0, hidden: 0 };
    pledges.forEach((p) => {
      c[p.status] = (c[p.status] || 0) + 1;
      if (p.hidden_from_wall) c.hidden += 1;
    });
    return c;
  }, [pledges]);

  const patchPledge = async (id, updates) => {
    setBusy(true);
    try {
      const res = await client.patch(`/admin/founding/${id}`, updates);
      // Merge updated row back into local state without a full refetch
      setPledges((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...res.data } : p))
      );
      return res.data;
    } finally {
      setBusy(false);
    }
  };

  const toggleHidden = async (pledge) => {
    try {
      await patchPledge(pledge.id, { hidden_from_wall: !pledge.hidden_from_wall });
      toast.success(
        pledge.hidden_from_wall
          ? `${pledge.full_name} is back on the wall.`
          : `${pledge.full_name} hidden from the public wall.`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update visibility.");
    }
  };

  const handleSaveEdit = async (updates) => {
    if (!editing) return;
    try {
      await patchPledge(editing.id, updates);
      toast.success(`Saved changes to ${editing.full_name}.`);
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save changes.");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await client.delete(`/admin/founding/${deleting.id}`);
      setPledges((prev) => prev.filter((p) => p.id !== deleting.id));
      toast.success(`Deleted ${deleting.full_name}.`);
      setDeleting(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete pledge.");
    } finally {
      setBusy(false);
    }
  };

  const sendEditLink = async (pledge) => {
    setBusy(true);
    try {
      const res = await client.post(`/admin/founding/${pledge.id}/edit-link`);
      const { sent, sent_to, edit_url, expires_at } = res.data;
      setPledges((prev) => prev.map((p) => (p.id === pledge.id ? { ...p, edit_link_issued_at: new Date().toISOString(), edit_token_expires_at: expires_at } : p)));
      let copied = false;
      try { await navigator.clipboard.writeText(edit_url); copied = true; } catch { /* clipboard unavailable */ }
      if (sent) {
        toast.success(`Edit link sent to ${sent_to}.`, { description: `Valid 30 days.${copied ? " Also copied to your clipboard." : ""}` });
      } else {
        toast.warning("Link created, but the email didn't go out.", { description: copied ? "The link is on your clipboard; paste it to the donor." : edit_url });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the edit link.");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || !user || !user.is_admin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-6 w-6 animate-spin text-olive" />
      </main>
    );
  }

  return (
    <main data-testid="admin-founding-page" className="bg-sand">
      <Navbar
        onStart={async () => {
          await logout();
          navigate("/");
        }}
        primaryLabel="Sign out"
        secondaryLabel={null}
      />

      <section className="mx-auto max-w-6xl px-6 pb-8 pt-32 lg:px-8 lg:pt-44">
        <FadeUp tier="sm">
          <PageAccent className="mb-5" />
        </FadeUp>
        <FadeUp>
          <button
            type="button"
            onClick={() => navigate("/admin")}
            data-testid="admin-founding-back"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-olive transition hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            back to admin
          </button>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} />
            Founding donors
          </span>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">
            The <EmSwash>founding-donor</EmSwash> ledger.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-olive">
            Every pledge — seed, pending, paid — in one place. Fix a typo,
            add a tribute or photo, hide a row from the public wall, or
            remove a test entry. Paid pledges can only be hidden, never
            deleted (tax-receipt integrity).
          </p>
        </FadeUp>

        <FoundingWindowPanel client={client} onChanged={refresh} />

        {/* Filter chips */}
        <div
          className="mt-8 flex flex-wrap gap-2"
          data-testid="admin-founding-filters"
        >
          {[
            { key: "all", label: "All" },
            { key: "paid", label: "Paid" },
            { key: "seed", label: "Seed" },
            { key: "pending", label: "Pending" },
            { key: "hidden", label: "Hidden" },
          ].map((f) => {
            const isActive = filter === f.key;
            const badge = counts[f.key] ?? 0;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`admin-founding-filter-${f.key}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold transition ${
                  isActive
                    ? "bg-ink text-sand"
                    : "border border-border bg-bone text-ink hover:bg-beige"
                }`}
              >
                {f.label}
                <span
                  className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] ${
                    isActive ? "bg-sand/20 text-sand" : "bg-ink/8 text-olive"
                  }`}
                >
                  {badge}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section
        className="mx-auto max-w-6xl px-6 pb-40 lg:px-8 lg:pb-48"
        data-testid="admin-founding-table-wrap"
      >
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-olive" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-scarlet/25 bg-scarlet/[0.05] p-6 text-scarlet">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-border bg-bone p-10 text-center text-olive">
            No pledges in this filter yet.
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-2xl border border-border bg-bone"
            data-testid="admin-founding-table"
          >
            <table className="w-full text-left text-[13px]">
              <thead className="bg-sand/60 text-[10px] uppercase tracking-[0.18em] text-olive">
                <tr>
                  <th className="px-4 py-3 font-semibold">Donor</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Tribute</th>
                  <th className="px-4 py-3 font-semibold">Wall</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`admin-founding-row-${p.id}`}
                    className="border-t border-border/60 hover:bg-sand/30"
                  >
                    {/* Donor */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {p.photo_url ? (
                          <img
                            src={p.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${p.photo_url}` : p.photo_url}
                            alt=""
                            className={`h-10 w-10 shrink-0 rounded-full object-cover ${p.photo_filter === "bw" ? "grayscale" : ""}`}
                          />
                        ) : (
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink/8 text-olive">
                            <UserCircle2 className="h-5 w-5" strokeWidth={1.5} />
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-ink">
                            {p.full_name || "—"}
                            {p.anonymous && (
                              <span className="ml-2 text-[10px] font-medium uppercase tracking-[0.14em] text-olive">
                                shown anon
                              </span>
                            )}
                            {p.source === "donation" && (
                              <span className="ml-2 rounded-full bg-yellow/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink" data-testid={`admin-founding-source-${p.id}`}>
                                from gift
                              </span>
                            )}
                            {p.refunded_at && (
                              <span className="ml-2 rounded-full bg-scarlet/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-scarlet" data-testid={`admin-founding-refunded-${p.id}`} title={`Refunded ${fmtMoney(p.refunded_cents)}`}>
                                refunded
                              </span>
                            )}
                          </p>
                          <p className="truncate text-[11px] text-olive">
                            {p.email || <span className="italic">no email</span>}
                          </p>
                          {p.alma_mater && (
                            <p className="truncate text-[11px] text-olive">
                              {p.alma_mater}
                            </p>
                          )}
                          {p.edit_link_issued_at && (
                            <p className="truncate text-[10px] text-cobalt" data-testid={`admin-founding-edit-link-meta-${p.id}`}>
                              edit link sent {new Date(p.edit_link_issued_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              {p.last_self_edit_at && ` · donor edited ${new Date(p.last_self_edit_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ring-1 ring-inset ${
                          STATUS_COLORS[p.status] || "bg-ink/8 text-olive ring-ink/15"
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>

                    {/* Amount */}
                    <td className="px-4 py-4 text-right font-mono text-ink">
                      {fmtMoney(p.amount_cents)}
                      {p.is_recurring && (
                        <span className="ml-1 text-[10px] uppercase text-olive">/mo</span>
                      )}
                    </td>

                    {/* Tribute */}
                    <td className="px-4 py-4 text-olive">
                      {p.tribute ? (
                        <span title={p.tribute}>{truncate(p.tribute, 72)}</span>
                      ) : (
                        <span className="italic text-olive/60">no tribute</span>
                      )}
                    </td>

                    {/* Wall visibility */}
                    <td className="px-4 py-4">
                      {p.status === "paid" || p.status === "seed" ? (
                        <button
                          type="button"
                          onClick={() => toggleHidden(p)}
                          disabled={busy}
                          data-testid={`admin-founding-toggle-hidden-${p.id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                            p.hidden_from_wall
                              ? "bg-ink/10 text-ink hover:bg-ink/15"
                              : "bg-grass/15 text-grass hover:bg-grass/25"
                          }`}
                        >
                          {p.hidden_from_wall ? (
                            <>
                              <EyeOff className="h-3 w-3" strokeWidth={2.2} />
                              Hidden
                            </>
                          ) : (
                            <>
                              <Eye className="h-3 w-3" strokeWidth={2.2} />
                              On wall
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="text-[11px] italic text-olive/70">
                          not yet live
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {(p.status === "paid" || p.status === "seed") && (
                          <button
                            type="button"
                            onClick={() => sendEditLink(p)}
                            disabled={busy || !p.email}
                            title={p.email ? "Email the donor a private 30-day link to edit their own entry" : "Add an email first"}
                            data-testid={`admin-founding-send-edit-link-${p.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-cobalt/30 bg-bone px-3 py-1 text-[11px] font-semibold text-cobalt transition hover:bg-cobalt/10 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Send className="h-3 w-3" strokeWidth={2.2} />
                            Send edit link
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditing(p)}
                          data-testid={`admin-founding-edit-${p.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-bone px-3 py-1 text-[11px] font-semibold text-ink transition hover:bg-beige"
                        >
                          <Pencil className="h-3 w-3" strokeWidth={2.2} />
                          Edit
                        </button>
                        {p.status !== "paid" && (
                          <button
                            type="button"
                            onClick={() => setDeleting(p)}
                            data-testid={`admin-founding-delete-${p.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-scarlet/30 bg-bone px-3 py-1 text-[11px] font-semibold text-scarlet transition hover:bg-scarlet/10"
                          >
                            <Trash2 className="h-3 w-3" strokeWidth={2.2} />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Footer
        onStart={async () => {
          await logout();
          navigate("/");
        }}
        onDonate={() => navigate("/admin")}
        primaryLabel="Sign out"
        secondaryLabel="Back to admin"
      />

      {/* Edit dialog */}
      <EditPledgeDialog
        pledge={editing}
        busy={busy}
        onSave={handleSaveEdit}
        onClose={() => setEditing(null)}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <AlertDialogContent data-testid="admin-founding-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleting?.full_name || "this pledge"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the row from the database entirely. For paid
              pledges use "Hide from wall" instead. This action is
              logged in the audit trail and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-founding-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={busy}
              data-testid="admin-founding-delete-confirm-btn"
              className="bg-scarlet text-bone hover:bg-scarlet/90"
            >
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

/* ── Edit dialog ─────────────────────────────────────────────────── */

const EditPledgeDialog = ({ pledge, busy, onSave, onClose }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!pledge) return;
    setForm({
      full_name: pledge.full_name || "",
      email: pledge.email || "",
      alma_mater: pledge.alma_mater || "",
      tribute: pledge.tribute || "",
      photo_url: pledge.photo_url || "",
      photo_filter: pledge.photo_filter || "color",
      anonymous: !!pledge.anonymous,
      hidden_from_wall: !!pledge.hidden_from_wall,
    });
  }, [pledge]);

  if (!pledge) return null;

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    // Only send fields that actually changed vs the source pledge
    const updates = {};
    const orig = {
      full_name: pledge.full_name || "",
      email: pledge.email || "",
      alma_mater: pledge.alma_mater || "",
      tribute: pledge.tribute || "",
      photo_url: pledge.photo_url || "",
      photo_filter: pledge.photo_filter || "color",
      anonymous: !!pledge.anonymous,
      hidden_from_wall: !!pledge.hidden_from_wall,
    };
    Object.keys(form).forEach((k) => {
      if (form[k] !== orig[k]) updates[k] = form[k];
    });
    onSave(updates);
  };

  return (
    <Dialog
      open={!!pledge}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="max-w-lg"
        data-testid="admin-founding-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit founding pledge</DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-ink">{pledge.full_name}</span>{" "}
            · status {pledge.status}
            {pledge.status === "paid" && (
              <span className="ml-2 text-[11px] italic text-olive">
                (paid pledges cannot be deleted — hide from wall instead)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form className="mt-2 space-y-4" onSubmit={submit}>
          <div>
            <Label htmlFor="edit-full-name">Full name</Label>
            <Input
              id="edit-full-name"
              data-testid="admin-founding-edit-full-name"
              value={form.full_name}
              onChange={(e) => setField("full_name", e.target.value)}
              placeholder="Terri James"
            />
          </div>

          <div>
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              data-testid="admin-founding-edit-email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="terri@example.com"
              type="email"
            />
          </div>

          <div>
            <Label htmlFor="edit-alma-mater">Alma mater</Label>
            <Input
              id="edit-alma-mater"
              data-testid="admin-founding-edit-alma-mater"
              value={form.alma_mater}
              onChange={(e) => setField("alma_mater", e.target.value)}
              placeholder="Spelman College"
            />
          </div>

          <div>
            <Label htmlFor="edit-tribute">Tribute (≤280 chars)</Label>
            <Textarea
              id="edit-tribute"
              data-testid="admin-founding-edit-tribute"
              rows={3}
              maxLength={280}
              value={form.tribute}
              onChange={(e) => setField("tribute", e.target.value)}
              placeholder="What this student's story means to them…"
            />
            <p className="mt-1 text-[11px] text-olive">
              {form.tribute?.length || 0} / 280
            </p>
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div>
              <Label htmlFor="edit-photo-url">Photo URL</Label>
              <Input
                id="edit-photo-url"
                data-testid="admin-founding-edit-photo-url"
                value={form.photo_url}
                onChange={(e) => setField("photo_url", e.target.value)}
                placeholder="https://… or /api/files/f_…"
              />
            </div>
            <div>
              <Label htmlFor="edit-photo-filter">Filter</Label>
              <Select
                value={form.photo_filter}
                onValueChange={(v) => setField("photo_filter", v)}
              >
                <SelectTrigger
                  id="edit-photo-filter"
                  data-testid="admin-founding-edit-photo-filter"
                  className="w-[110px]"
                >
                  <SelectValue placeholder="filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="color">Color</SelectItem>
                  <SelectItem value="bw">Black &amp; white</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-border bg-sand/40 p-3">
            <div>
              <p className="text-[13px] font-semibold text-ink">Show as anonymous</p>
              <p className="text-[11px] text-olive">
                Public wall shows "Anonymous" instead of their name.
              </p>
            </div>
            <Switch
              checked={form.anonymous}
              onCheckedChange={(v) => setField("anonymous", !!v)}
              data-testid="admin-founding-edit-anonymous"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-border bg-sand/40 p-3">
            <div>
              <p className="text-[13px] font-semibold text-ink">
                Hide from public wall
              </p>
              <p className="text-[11px] text-olive">
                Row stays in the database, but drops off the public wall.
              </p>
            </div>
            <Switch
              checked={form.hidden_from_wall}
              onCheckedChange={(v) => setField("hidden_from_wall", !!v)}
              data-testid="admin-founding-edit-hidden"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="admin-founding-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-founding-edit-save"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
